/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';

/**
 * Why the guest was stopped. Most of these are not about access at all - an event that is not on offer or a
 * suite that has not been set up are configuration, and a check that could not be made is neither - so the
 * reason travels with the state and decides what the guest is told.
 */
export type AccessErrorReason =
    | 'not-signed-in'
    | 'no-suite-access'
    | 'no-customer-account'
    | 'event-not-available'
    | 'suite-not-configured'
    | 'service-unavailable'
    | 'session-expired';

/** The guest has an order here the site could not open, so the page gives way as it does for access. */
export type OrderErrorReason = 'order-not-opened';

export interface IAccessErrorState {
    isAccessError: boolean;
    reason?: AccessErrorReason | OrderErrorReason;

    /** The heading over the message, where the reason is not about access. */
    title?: string;

    /** Resolved where the reason is known, so every module shows the same words without repeating them. */
    message?: string;
}

export class AccessErrorStateInput implements IActionInput {
    public getCacheKey = () => `AccessErrorState`;
    public getCacheObjectType = () => 'AccessErrorState';
    public dataCacheType = (): CacheType => 'request';
}

async function accessErrorStateAction(input: AccessErrorStateInput, ctx: IActionContext): Promise<IAccessErrorState> {
    return { isAccessError: false };
}

const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    return new AccessErrorStateInput();
};

export default createObservableDataAction<IAccessErrorState>({
    action: <IAction<IAccessErrorState>>accessErrorStateAction,
    input: createInput
});
